import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import type { BizyUserConversation, BizyUserConversationDetail, BizyUserMessage } from '../lib/bizy/types';
import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	Conversations = 'bizy-user-conversations',
	Conversation = 'bizy-user-conversation',
}

export const bizyUserConversationsQueryKey = (params?: Record<string, unknown>) => [QueryKey.Conversations, params].filter(Boolean);

export const bizyUserConversationQueryKey = (id?: string) => [QueryKey.Conversation, id].filter(Boolean);

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyConversationsPath = (prefix = '') => `${prefix}/bizy/conversations`;
export const bizyConversationPath = (id: string, prefix = '') => `${prefix}/bizy/conversations/${id}`;

// ─── API response types ──────────────────────────────────────────────────────

interface BizyConversationsIndexAPIResponse {
	conversations: Array<Record<string, unknown>>;
	page: number;
	perPage: number;
	totalCount: number;
	totalPages: number;
}

interface BizyConversationDetailAPIResponse {
	conversation: {
		conversationId: string;
		pagePath: string | null;
		sharedWithAdmin: boolean;
		messages: Array<Record<string, unknown>>;
	};
}

// ─── Pagination metadata ─────────────────────────────────────────────────────

export class BizyConversationsMeta {
	page = 1;
	perPage = 25;
	totalCount = 0;
	totalPages = 0;

	constructor(data: Partial<BizyConversationsMeta> = {}) {
		Object.assign(this, data);
	}
}

// ─── Query params ────────────────────────────────────────────────────────────

export interface BizyConversationsParams {
	page?: number;
	per_page?: number;
}

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches paginated list of the current user's Bizy conversations.
 *
 * GET /api/v2/bizy/conversations
 */
export const useBizyUserConversations = (params?: BizyConversationsParams, options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyUserConversationsQueryKey(params as Record<string, unknown>),
		queryFn: async () => {
			const res = await http.get<BizyConversationsIndexAPIResponse>(bizyConversationsPath(), {
				params,
				headers,
			});

			return [
				(res.data.conversations as unknown as BizyUserConversation[]).map((c) => ({ ...c, sharedWithAdmin: c.sharedWithAdmin ?? false })),
				new BizyConversationsMeta({
					page: res.data.page,
					perPage: res.data.perPage,
					totalCount: res.data.totalCount,
					totalPages: res.data.totalPages,
				}),
			] as const;
		},
		enabled: options?.enabled ?? true,
		keepPreviousData: true,
	});
};

/**
 * Fetches full conversation detail with all messages.
 *
 * GET /api/v2/bizy/conversations/:id
 */
export const useBizyUserConversation = (conversationId: string | undefined) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyUserConversationQueryKey(conversationId),
		queryFn: async () => {
			// conversationId is guaranteed by `enabled: !!conversationId` below
			const res = await http.get<BizyConversationDetailAPIResponse>(bizyConversationPath(conversationId as string), {
				headers,
			});

			const conv = res.data.conversation;

			return {
				conversationId: conv.conversationId,
				pagePath: conv.pagePath,
				sharedWithAdmin: conv.sharedWithAdmin ?? false,
				messages: conv.messages as unknown as BizyUserMessage[],
			} as BizyUserConversationDetail;
		},
		enabled: !!conversationId,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Updates a conversation's title (rename or AI-generated).
 *
 * PATCH /api/v2/bizy/conversations/:id
 */
export const useBizyUpdateConversationMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ conversationId, title }: { conversationId: string; title: string }): Promise<{ conversation_id: string; title: string }> => {
			const res = await http.patch<{ conversation_id: string; title: string }>(bizyConversationPath(conversationId), { title });
			return res.data;
		},
		onSuccess: (_data, { conversationId }) => {
			queryClient.invalidateQueries(bizyUserConversationsQueryKey());
			queryClient.invalidateQueries(bizyUserConversationQueryKey(conversationId));
		},
	});
};

/**
 * Shares or unshares a Bizy conversation with the internal admin review tools.
 *
 * PATCH /api/v2/bizy/conversations/:id/share
 */
export const useBizyShareConversationMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			conversationId,
			shared,
		}: {
			conversationId: string;
			shared: boolean;
		}): Promise<{ conversation_id: string; shared_with_admin: boolean }> => {
			const res = await http.patch<{ conversation_id: string; shared_with_admin: boolean }>(`${bizyConversationPath(conversationId)}/share`, { shared });
			return res.data;
		},
		onSuccess: (data, { conversationId }) => {
			queryClient.setQueryData<BizyUserConversationDetail | undefined>(bizyUserConversationQueryKey(conversationId), (current) =>
				current ? { ...current, sharedWithAdmin: data.shared_with_admin } : current
			);
			queryClient.invalidateQueries(bizyUserConversationsQueryKey());
			queryClient.invalidateQueries(bizyUserConversationQueryKey(conversationId));
		},
	});
};

/**
 * Deletes a Bizy conversation (soft-delete on server).
 *
 * DELETE /api/v2/bizy/conversations/:id
 */
export const useBizyDeleteConversationMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (conversationId: string): Promise<void> => {
			await http.delete(bizyConversationPath(conversationId));
		},
		onSuccess: (_data, conversationId) => {
			queryClient.invalidateQueries(bizyUserConversationsQueryKey());
			queryClient.removeQueries(bizyUserConversationQueryKey(conversationId));
		},
	});
};
